import AccountMappingApproval from "./AccountMappingApproval";
import DomainSourceUpload from "./DomainSourceUpload";
import { useCallback, useEffect, useState } from "react";
import MonthlyBridge from "./MonthlyBridge";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PackageSearch, FileDown } from "lucide-react";

const STATUS_STYLE = {
  RECONCILED: "bg-green-100 text-green-800 border-green-300",
  COMPLETE: "bg-green-100 text-green-800 border-green-300",
  APPROVED: "bg-green-100 text-green-800 border-green-300",
  PASS: "bg-green-100 text-green-800 border-green-300",
  PASS_WITH_REVIEW_ITEMS: "bg-amber-100 text-amber-800 border-amber-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  NO_CANDIDATES: "bg-green-100 text-green-800 border-green-300",
  NOT_PERFORMED_NO_COMMON_REFERENCE: "bg-slate-100 text-slate-700 border-slate-300",
  AMBIGUOUS: "bg-red-100 text-red-800 border-red-300",
  READY: "bg-green-100 text-green-800 border-green-300",
  READY_FOR_FULL_CYCLE: "bg-green-100 text-green-800 border-green-300",
  PARTIAL: "bg-amber-100 text-amber-800 border-amber-300",
  BLOCKED: "bg-red-100 text-red-800 border-red-300",
  NOT_READY_FOR_FULL_CYCLE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_SOURCE: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MAPPING: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_PERIOD: "bg-red-100 text-red-800 border-red-300",
  SOURCE_NOT_SUPPLIED: "bg-slate-100 text-slate-700 border-slate-300",
};
const ACCEPTANCE_LABELS = {
  PASS: "Diterima",
  PASS_WITH_REVIEW_ITEMS: "Diterima dengan pos reviu",
  BLOCKED_SOURCE: "Tertahan — sumber pembelian belum tersedia",
  BLOCKED_MAPPING: "Tertahan — pemetaan akun belum disetujui",
  BLOCKED_PERIOD: "Tertahan — periode belum lengkap",
  REVIEW_REQUIRED: "Perlu reviu",
};
const ITEM_LABELS = {
  MATCHED_EXACT: "Cocok tepat",
  MATCHED_ONE_TO_MANY: "Satu ke banyak",
  MATCHED_MANY_TO_ONE: "Banyak ke satu",
  MATCHED_PARTIAL: "Cocok sebagian",
  TIMING_DIFFERENCE: "Perbedaan waktu",
  DATE_DIFFERENCE: "Perbedaan tanggal",
  AMOUNT_DIFFERENCE: "Perbedaan nilai",
  VENDOR_MISMATCH: "Pemasok berbeda",
  PURCHASE_ONLY: "Hanya di pembelian",
  GL_ONLY: "Hanya di buku besar",
  MATCHED_PURCHASE_VAT: "Cocok dengan PPN Masukan",
  PURCHASE_WITHOUT_VAT: "Pembelian tanpa faktur pajak",
  VAT_WITHOUT_PURCHASE: "Faktur pajak tanpa pembelian",
  VAT_AMOUNT_DIFFERENCE: "Perbedaan nilai PPN",
  VAT_DATE_DIFFERENCE: "Perbedaan tanggal PPN",
  VAT_TIMING_DIFFERENCE: "Perbedaan masa pengkreditan",
  VAT_VENDOR_MISMATCH: "Pemasok berbeda",
  VAT_NON_CREDITABLE: "Belum dikreditkan",
  VAT_NOT_APPLICABLE: "PPN tidak berlaku",
  MATCHED_PURCHASE_AP: "Cocok dengan hutang",
  FULLY_SETTLED: "Telah dibayar",
  PARTIALLY_SETTLED: "Dibayar sebagian",
  AP_ONLY: "Hanya di hutang",
  FULLY_SETTLED_OR_CASH_PURCHASE_CANDIDATE: "Kandidat lunas / pembelian tunai",
  SUBSEQUENTLY_PAID_FULL: "Dibayar penuh setelah periode",
  SUBSEQUENTLY_PAID_PARTIAL: "Dibayar sebagian setelah periode",
  UNPAID_AFTER_PERIOD: "Belum dibayar",
  PAYMENT_WITHOUT_AP: "Pembayaran tanpa hutang periode ini",
  UNRECORDED_LIABILITY_CANDIDATE: "Kandidat hutang belum tercatat",
  REVIEW_REQUIRED: "Perlu reviu",
};
const NOT_AVAILABLE = "Tidak berlaku / sumber tidak tersedia";

const GATE_LABELS = {
  population_complete: "Populasi pembelian lengkap",
  vat_and_non_vat_included: "Mencakup pembelian ber-PPN dan tanpa PPN",
  purchase_value_available: "Nilai pembelian per dokumen tersedia",
  stable_source_references: "Referensi dokumen sumber stabil",
  population_reconciles_to_gl: "Populasi sesuai buku besar",
  sampling_universe: "Kerangka sampling tersedia",
  jet_universe: "Populasi jurnal tersedia",
  supporting_documents: "Dokumen pendukung tersedia",
  cutoff_testable: "Cut-off dapat diuji",
  limitations_disclosed: "Keterbatasan dinyatakan",
  ap_invoice_level: "Hutang usaha per faktur",
  unrecorded_liability_testable: "Hutang belum tercatat dapat diuji",
};
const READINESS_LABELS = {
  READY: "Siap", PARTIAL: "Sebagian", BLOCKED: "Tertahan",
  READY_FOR_FULL_CYCLE: "Siap untuk audit siklus penuh",
  NOT_READY_FOR_FULL_CYCLE: "Belum siap untuk audit siklus penuh",
};

const TABS = [
  ["ringkasan", "Ringkasan", () => true],
  ["populasi", "Populasi Pembelian", (v) => v.population?.controls?.rows > 0],
  ["gl", "Purchase GL", (v) => !!v.purchase_vs_gl],
  ["vsgl", "Pembelian vs GL", (v) => !!v.purchase_vs_gl],
  ["vsvat", "Pembelian vs PPN Masukan", (v) => !!v.purchase_vs_vat],
  ["vsap", "Pembelian vs Hutang Usaha", (v) => !!v.purchase_vs_ap],
  ["aging", "Aging Hutang", (v) => !!v.ap_tie_out],
  ["bayar", "Pembayaran Setelah Periode", (v) => !!v.subsequent_payment],
  ["hutang", "Potensi Hutang Belum Tercatat", (v) => !!v.unrecorded_liabilities],
  ["pos", "Pos Rekonsiliasi", (v) => !!v.purchase_vs_gl],
  ["bulanan", "Bulanan → Tahunan", (v) => !!v.purchase_vs_gl],
  ["cutoff", "Cut-off", () => true],
  ["retur", "Retur / Diskon", (v) => v.returns?.status === "PRESENT"],
  ["kesiapan", "Kesiapan Siklus Penuh", (v) => !!v.full_cycle_readiness],
  ["bukti", "Bukti Audit", () => true],
];

// Pembelian / Hutang Usaha — satu populasi audit untuk periode audit yang dideklarasikan.
export default function PurchasesPayables({ id }) {
  const [view, setView] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("ringkasan");

  const load = useCallback(() => {
    api.get(`/engagements/${id}/purchases`)
      .then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
    api.get(`/engagements/${id}/purchases-summary`)
      .then(({ data }) => setSummary(data))
      .catch(() => setSummary(null));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const exportWorkpaper = () =>
    downloadFile(`/engagements/${id}/purchases/workpaper.xlsx`, `kertas-kerja-pembelian-${id}.xlsx`)
      .catch((e) => toast.error(getErrorMessage(e)));

  if (!view) return <div className="text-xs text-muted-foreground p-4">Memuat data pembelian…</div>;
  const available = TABS.filter(([, , when]) => when(view));

  return (
    <div className="space-y-4" data-testid="purchases-payables">
      <DomainSourceUpload id={id} endpoint="purchase-sources" label="Unggah sumber pembelian (rincian pembelian, register/B2 PPN masukan, umur hutang, pembayaran setelah neraca)" accept=".xlsx,.xls,.csv" onUploaded={load} />
      <AccountMappingApproval id={id} path="payable-accounts" title="Pemetaan akun pembelian dan hutang usaha" testid="purchase-mapping-approval" onApproved={load}
        groups={[{ key: "payable_accounts", candidates: "payable_candidates", label: "Akun hutang usaha", required: true },
                 { key: "vat_input_accounts", candidates: "vat_input_candidates", label: "Akun PPN masukan" },
                 { key: "purchase_debit_accounts", candidates: "purchase_debit_candidates", label: "Akun pembelian / persediaan / beban yang didebit" }]} />
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2">
          <PackageSearch size={15} />Pembelian / Hutang Usaha
        </div>
        <div className="text-xs text-muted-foreground">{view.audit_period_display}</div>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportWorkpaper}
          data-testid="purchases-export">
          <FileDown size={14} className="mr-1" />Ekspor Kertas Kerja
        </Button>
      </div>

      {summary && (
        <div className="bg-white border border-border rounded-sm p-2" data-testid="purchases-domain-summary">
          <div className="text-xs font-head font-semibold px-1 pb-2">
            Ringkasan Domain Pembelian
            <span className={`ml-2 font-normal inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[summary.acceptance] || ""}`}>
              {ACCEPTANCE_LABELS[summary.acceptance] || summary.acceptance}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <Figure label="Sumber data" value={`${summary.sources} berkas`} />
            <Figure label="Dokumen pembelian" value={summary.documents} />
            <Figure label="Jumlah pemasok" value={summary.vendors} />
            <Figure label="PPN Masukan (register)" value={rupiah(summary.input_vat)} />
            <Figure label="Kelengkapan periode" value={summary.coverage} status={summary.coverage_status} />
            <Figure label="Baris di luar periode" value={summary.out_of_period_rows} />
            <Figure label="Hutang usaha buku besar" value={rupiah(summary.payable_gl)} />
            <Figure label="Pemetaan akun" value={summary.account_mapping_status} status={summary.account_mapping_status} />
            <Figure label="Pembelian ↔ GL" value={statusText(summary.purchase_vs_gl_status)} status={summary.purchase_vs_gl_status} />
            <Figure label="Pembelian ↔ PPN Masukan" value={statusText(summary.purchase_vs_vat_status)} status={summary.purchase_vs_vat_status} />
            <Figure label="Pembelian ↔ Hutang" value={statusText(summary.purchase_vs_ap_status)} status={summary.purchase_vs_ap_status} />
            <Figure label="Saldo umur hutang" value={summary.ap_closing ? rupiah(summary.ap_closing) : NOT_AVAILABLE} />
            <Figure label="Pembayaran setelah periode" value={statusText(summary.subsequent_payment_status)} status={summary.subsequent_payment_status} />
            <Figure label="Kandidat hutang belum tercatat" value={summary.unrecorded_liability_candidates ?? NOT_AVAILABLE}
              status={summary.unrecorded_liability_status} />
            <Figure label="Pos cut-off" value={summary.cutoff_exceptions} />
            <Figure label="Pembelian belum cocok" value={summary.unmatched_purchase ?? "—"} />
            <Figure label="Buku besar belum cocok" value={summary.unmatched_gl ?? "—"} />
            <Figure label="PPN belum cocok" value={summary.unmatched_vat ?? "—"} />
          </div>
          {summary.carries_taxable_base === false && (
            <div className="text-xs text-amber-800 px-1 pt-2" data-testid="purchases-base-note">
              Sumber pembelian memuat PPN Masukan per dokumen, bukan DPP; nilai pembelian tidak dapat
              diuji dari sumber yang tersedia.
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-border rounded-sm">
        <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1">
          {available.map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`purchases-tab-${key}`}
              className={`text-xs px-2 py-1 rounded-sm ${tab === key ? "bg-slate-200 font-medium" : "hover:bg-slate-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto p-2" data-testid={`purchases-panel-${tab}`}>
          {tab === "ringkasan" && <Sources rows={view.sources} mapping={view.account_mapping} />}
          {tab === "populasi" && <Population population={view.population} />}
          {tab === "gl" && <Allocations rows={view.purchase_vs_gl?.allocations} clearing={view.gl_clearing_rows_excluded} />}
          {tab === "vsgl" && <Items rows={view.purchase_vs_gl?.purchase_items} origin="vsgl" />}
          {tab === "vsvat" && <VatPairs result={view.purchase_vs_vat} control={view.vat_control} />}
          {tab === "vsap" && <Payable result={view.purchase_vs_ap} />}
          {tab === "aging" && <TieOut tie={view.ap_tie_out} result={view.purchase_vs_ap} />}
          {tab === "bayar" && <Payments result={view.subsequent_payment} />}
          {tab === "hutang" && <Unrecorded result={view.unrecorded_liabilities} />}
          {tab === "pos" && <Items rows={unresolved(view)} origin="pos" />}
          {tab === "bulanan" && <MonthlyBridge bridge={view.monthly_bridge} lumpsum={view.lumpsum} sourceLabel="Pembelian" sourceKey="purchases" prefix="purchase" />}
          {tab === "cutoff" && <Cutoff result={view.cutoff} windows={view.cutoff_windows} />}
          {tab === "retur" && <Returns result={view.returns} />}
          {tab === "kesiapan" && <Readiness readiness={view.full_cycle_readiness}
            ledger={view.ledger_population} sampling={view.sampling_universe}
            journals={view.journal_universe} requests={view.missing_sources} />}
          {tab === "bukti" && <Evidence rows={view.evidence} />}
        </div>
      </div>
    </div>
  );
}

function statusText(status) {
  if (status === "SOURCE_NOT_SUPPLIED") return NOT_AVAILABLE;
  return ITEM_LABELS[status] || status;
}

function unresolved(view) {
  const purchase = (view.purchase_vs_gl?.purchase_items || []).filter((r) =>
    ["PURCHASE_ONLY", "AMOUNT_DIFFERENCE", "REVIEW_REQUIRED"].includes(r.status));
  const gl = (view.purchase_vs_gl?.gl_items || []).filter((r) =>
    ["GL_ONLY", "AMOUNT_DIFFERENCE", "REVIEW_REQUIRED"].includes(r.status));
  return purchase.concat(gl);
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Sources({ rows = [], mapping }) {
  return (
    <div className="space-y-3">
      <table className="w-full audit-table text-xs" data-testid="purchases-sources">
        <thead><tr><th>Berkas</th><th>Sheet</th><th>Jenis sumber</th><th className="text-right">Baris</th>
          <th>Periode sumber</th><th>Catatan</th></tr></thead>
        <tbody>{rows.map((row) => (
          <tr key={row.id}>
            <td>{row.filename}</td><td>{row.sheet}</td><td>{row.source_type}</td>
            <td className="text-right tabular-nums">{row.rows}</td>
            <td>{row.declared_range?.stated || row.as_of || "—"}</td>
            <td className="max-w-md">{(row.notes || []).join(" ") || "—"}</td>
          </tr>))}
        </tbody>
      </table>
      <div className="text-xs" data-testid="purchases-mapping">
        <div className="font-medium mb-1">Pemetaan akun</div>
        <div>Hutang usaha: {(mapping?.payable_accounts || []).join(", ") || "belum disetujui"}</div>
        <div>PPN Masukan: {(mapping?.vat_input_accounts || []).join(", ") || "—"}</div>
        <div>Akun debit pembelian: {(mapping?.purchase_debit_accounts || []).join(", ") || "—"}</div>
      </div>
    </div>
  );
}

function Population({ population }) {
  const controls = population.controls || {};
  return (
    <div className="space-y-2 text-xs" data-testid="purchases-population">
      <div>Bulan tercakup: {(population.months || []).join(", ") || "—"} ({population.coverage})</div>
      {(population.missing_months || []).length > 0 && (
        <div className="text-red-700">Bulan belum tersedia: {population.missing_months.join(", ")}</div>
      )}
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Dokumen dalam periode audit</td><td className="text-right tabular-nums">{population.counts?.in_period}</td></tr>
          <tr><td>Dokumen setelah periode</td><td className="text-right tabular-nums">{population.counts?.after_period}</td></tr>
          <tr><td>Dokumen sebelum periode</td><td className="text-right tabular-nums">{population.counts?.before_period}</td></tr>
          <tr><td>Jumlah pemasok</td><td className="text-right tabular-nums">{controls.vendors}</td></tr>
          <tr><td>PPN Masukan</td><td className="text-right tabular-nums">{rupiah(controls.input_vat)}</td></tr>
        </tbody>
      </table>
    </div>
  );
}

function Allocations({ rows = [], clearing }) {
  return (
    <div className="space-y-2">
      {clearing > 0 && (
        <div className="text-xs text-muted-foreground" data-testid="purchases-clearing-note">
          {clearing} baris pemindahbukuan PPN pada akun PPN Masukan dikeluarkan dari populasi
          pembelian dan dilaporkan terpisah.
        </div>
      )}
      <table className="w-full audit-table text-xs" data-testid="purchases-allocations">
        <thead><tr><th>Referensi</th><th>Dokumen</th><th>Buku besar</th><th className="text-right">Pembelian</th>
          <th className="text-right">Buku besar</th><th className="text-right">Selisih</th><th>Aturan</th><th>Bukti</th></tr></thead>
        <tbody>{rows.slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.key}</td><td>{(row.purchase_ids || []).slice(0, 3).join(", ")}</td>
            <td>{(row.gl_ids || []).slice(0, 3).join(", ")}</td>
            <td className="text-right tabular-nums">{rupiah(row.purchase_amount)}</td>
            <td className="text-right tabular-nums">{rupiah(row.gl_amount)}</td>
            <td className="text-right tabular-nums">{rupiah(row.difference)}</td>
            <td>{ITEM_LABELS[row.rule] || row.rule}</td>
            <td>{(row.evidence || []).join(", ")}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Items({ rows = [], origin }) {
  if (!rows.length) {
    return <div className="text-xs text-muted-foreground p-2" data-testid={`purchases-items-${origin}-empty`}>
      Tidak ada pos pada kategori ini.
    </div>;
  }
  return (
    <table className="w-full audit-table text-xs" data-testid={`purchases-items-${origin}`}>
      <thead><tr><th>ID</th><th>Tanggal</th><th>Pemasok</th><th className="text-right">Nilai</th>
        <th className="text-right">Sisa</th><th>Status</th><th>Catatan</th></tr></thead>
      <tbody>{rows.slice(0, 200).map((row, index) => (
        <tr key={`${row.id}-${index}`}>
          <td>{row.id}</td><td>{row.date}</td><td>{row.vendor}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.remaining)}</td>
          <td>{ITEM_LABELS[row.status] || row.status}</td>
          <td className="max-w-sm">{(row.notes || []).join(" ")}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function VatPairs({ result, control }) {
  if (!result) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="purchases-vat-empty">{NOT_AVAILABLE}</div>;
  }
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground" data-testid="purchases-vat-basis">
        {result.parameters?.basis}
      </div>
      <table className="w-full audit-table text-xs" data-testid="purchases-vat-control">
        <thead><tr><th>Masa</th><th className="text-right">PPN register</th>
          <th className="text-right">PPN Masukan B2</th><th className="text-right">Selisih</th></tr></thead>
        <tbody>{(control?.months || []).map((row) => (
          <tr key={row.month}>
            <td>{row.month}</td>
            <td className="text-right tabular-nums">{rupiah(row.register_vat)}</td>
            <td className="text-right tabular-nums">{rupiah(row.b2_vat)}</td>
            <td className="text-right tabular-nums">{rupiah(row.difference)}</td>
          </tr>))}
          {control?.total && (
            <tr className="font-medium">
              <td>Total periode</td>
              <td className="text-right tabular-nums">{rupiah(control.total.register_vat)}</td>
              <td className="text-right tabular-nums">{rupiah(control.total.b2_vat)}</td>
              <td className="text-right tabular-nums">{rupiah(control.total.difference)}</td>
            </tr>)}
        </tbody>
      </table>
      <Items rows={(result.purchase_items || []).filter((r) => r.status !== "MATCHED_PURCHASE_VAT")} origin="vat" />
    </div>
  );
}

function Payable({ result }) {
  if (!result) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="purchases-ap-empty">{NOT_AVAILABLE}</div>;
  }
  return (
    <div className="space-y-3 text-xs" data-testid="purchases-ap">
      <div>Tingkat data sumber: <span className="font-medium">{result.granularity}</span></div>
      {result.summary?.limitation && (
        <div className="text-amber-800" data-testid="purchases-ap-limitation">{result.summary.limitation}</div>
      )}
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Pemasok</th><th className="text-right">Saldo hutang</th>
          <th className="text-right">Dokumen pembelian</th><th>Status</th><th>Catatan</th></tr></thead>
        <tbody>{(result.ap_items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.vendor}</td>
            <td className="text-right tabular-nums">{rupiah(row.outstanding)}</td>
            <td className="text-right tabular-nums">{row.purchase_documents ?? "—"}</td>
            <td>{ITEM_LABELS[row.status] || row.status}</td>
            <td className="max-w-md">{(row.notes || []).join(" ")}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function TieOut({ tie, result }) {
  if (!tie) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="purchases-tieout-empty">{NOT_AVAILABLE}</div>;
  }
  return (
    <div className="space-y-2 text-xs" data-testid="purchases-tieout">
      <table className="w-full audit-table text-xs">
        <tbody>
          <tr><td>Umur hutang per tanggal pelaporan</td><td className="text-right tabular-nums">{rupiah(tie.aging_total)}</td></tr>
          <tr><td>Saldo hutang buku besar</td><td className="text-right tabular-nums">{rupiah(tie.gl_total)}</td></tr>
          <tr><td>Selisih</td><td className="text-right tabular-nums">{rupiah(tie.difference)}</td></tr>
        </tbody>
      </table>
      <div className="text-muted-foreground">{tie.basis}</div>
      {result?.summary?.limitation && <div className="text-amber-800">{result.summary.limitation}</div>}
    </div>
  );
}

function Payments({ result }) {
  if (!result) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="purchases-payments-empty">{NOT_AVAILABLE}</div>;
  }
  const summary = result.summary || {};
  return (
    <div className="space-y-2 text-xs" data-testid="purchases-payments">
      <div>
        {summary.invoices} faktur · {summary.payment_events} transaksi pembayaran ·{" "}
        {summary.events_settling_several_invoices} pembayaran melunasi lebih dari satu faktur
      </div>
      <div className="text-muted-foreground">{summary.note}</div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>No. Faktur</th><th>Tgl Faktur</th><th>Pemasok</th><th className="text-right">Nilai faktur</th>
          <th>Tgl pembayaran</th><th className="text-right">Nilai pembayaran</th><th>Status</th></tr></thead>
        <tbody>{(result.items || []).slice(0, 200).map((row, index) => (
          <tr key={index}>
            <td>{row.invoice_no}</td><td>{row.invoice_date}</td><td>{row.vendor}</td>
            <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
            <td>{row.payment_date}</td>
            <td className="text-right tabular-nums">{rupiah(row.payment_amount)}</td>
            <td>{ITEM_LABELS[row.status] || row.status}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Unrecorded({ result }) {
  if (!result) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="purchases-unrecorded-empty">{NOT_AVAILABLE}</div>;
  }
  return (
    <div className="space-y-2 text-xs" data-testid="purchases-unrecorded">
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Uji</th><th>Hasil</th></tr></thead>
        <tbody>{Object.entries(result.tests || {}).map(([name, value]) => (
          <tr key={name}><td>{name}</td><td>{String(value)}</td></tr>))}
        </tbody>
      </table>
      <div className="text-muted-foreground">{result.note}</div>
      {(result.items || []).length === 0 ? (
        <div className="p-2">Tidak ada kandidat hutang belum tercatat dari sumber yang tersedia.</div>
      ) : (
        <table className="w-full audit-table text-xs">
          <thead><tr><th>No. Faktur</th><th>Tgl Faktur</th><th>Pemasok</th><th className="text-right">Nilai</th>
            <th>Tgl pembayaran</th><th>Indikator</th><th>Simpulan auditor</th></tr></thead>
          <tbody>{result.items.slice(0, 200).map((row, index) => (
            <tr key={index}>
              <td>{row.invoice_no}</td><td>{row.invoice_date}</td><td>{row.vendor}</td>
              <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
              <td>{row.payment_date}</td><td className="max-w-md">{row.indicator}</td>
              <td className="text-muted-foreground">Belum disimpulkan</td>
            </tr>))}
          </tbody>
        </table>)}
    </div>
  );
}

function Cutoff({ result, windows }) {
  const items = result?.items || [];
  return (
    <div className="space-y-2 text-xs" data-testid="purchases-cutoff">
      <div>
        Jendela reviu:{" "}
        {Object.entries(windows || {}).map(([days, row]) => `±${days} hari: ${row.count}`).join(" · ") || "—"}
      </div>
      <div className="text-muted-foreground">{result?.note}</div>
      {items.length === 0 ? <div className="p-2">Tidak ada pos cut-off pada jendela ini.</div> : (
        <table className="w-full audit-table text-xs">
          <thead><tr><th>Dokumen</th><th>Tanggal</th><th>Pemasok</th><th className="text-right">Nilai</th>
            <th>Klasifikasi</th><th className="text-right">Hari dari akhir periode</th><th>Simpulan auditor</th></tr></thead>
          <tbody>{items.slice(0, 200).map((row, index) => (
            <tr key={index}>
              <td>{row.invoice_no}</td><td>{row.date}</td><td>{row.counterparty}</td>
              <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
              <td>{row.cutoff}</td>
              <td className="text-right tabular-nums">{row.days_from_period_end}</td>
              <td className="text-muted-foreground">Belum disimpulkan</td>
            </tr>))}
          </tbody>
        </table>)}
    </div>
  );
}

function Returns({ result }) {
  if (!result || result.status !== "PRESENT") {
    return <div className="text-xs text-muted-foreground p-2" data-testid="purchases-returns-empty">
      Sumber tidak memuat retur, diskon atau nota kredit pembelian.
    </div>;
  }
  return (
    <table className="w-full audit-table text-xs" data-testid="purchases-returns">
      <thead><tr><th>Dokumen</th><th>Tanggal</th><th>Pemasok</th><th className="text-right">Nilai</th>
        <th>Klasifikasi</th></tr></thead>
      <tbody>{(result.items || []).map((row, index) => (
        <tr key={index}>
          <td>{row.invoice_no}</td><td>{row.date}</td><td>{row.vendor}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td>{row.classification}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Readiness({ readiness, ledger, sampling, journals, requests }) {
  if (!readiness) return <div className="text-xs text-muted-foreground p-2">Tidak berlaku / sumber tidak tersedia</div>;
  const row = ([name, entry]) => (
    <tr key={name}>
      <td>{GATE_LABELS[name] || name}</td>
      <td><span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[entry.status] || ""}`}>
        {READINESS_LABELS[entry.status] || entry.status}</span></td>
      <td className="max-w-xl">{entry.basis}{entry.coverage ? ` (${entry.coverage})` : ""}</td>
    </tr>);
  return (
    <div className="space-y-3 text-xs" data-testid="purchase-readiness">
      <div className="font-medium">
        <span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[readiness.status] || ""}`}
          data-testid="purchase-readiness-status">
          {READINESS_LABELS[readiness.status] || readiness.status}
        </span>
        <span className="ml-2 font-normal text-muted-foreground">
          Cakupan dokumen pendukung {readiness.documentary_evidence_coverage}
        </span>
      </div>
      <div className="text-muted-foreground">{readiness.basis}</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Figure label="Dokumen pembelian" value={ledger?.controls?.documents} />
        <Figure label="Ber-PPN / tanpa PPN"
          value={`${ledger?.controls?.documents_with_input_vat ?? "—"} / ${ledger?.controls?.documents_without_input_vat ?? "—"}`} />
        <Figure label="Kerangka sampling" value={sampling?.count} />
        <Figure label="Baris jurnal siklus" value={journals?.rows} />
      </div>
      <div className="text-muted-foreground">{ledger?.basis}</div>
      <table className="w-full audit-table text-xs" data-testid="purchase-readiness-gates">
        <thead><tr><th>Gerbang</th><th>Status</th><th>Dasar</th></tr></thead>
        <tbody>{Object.entries(readiness.gates || {}).map(row)}</tbody>
      </table>
      <div className="font-medium">Kemampuan prosedur (keterbatasan, bukan kegagalan siklus)</div>
      <table className="w-full audit-table text-xs" data-testid="purchase-readiness-capabilities">
        <thead><tr><th>Kemampuan</th><th>Status</th><th>Dasar</th></tr></thead>
        <tbody>{Object.entries(readiness.capabilities || {}).map(row)}</tbody>
      </table>
      {!!(requests || []).length && (
        <>
          <div className="font-medium">Permintaan sumber yang belum tersedia</div>
          <table className="w-full audit-table text-xs" data-testid="purchase-missing-sources">
            <thead><tr><th>Sumber</th><th>Field yang diperlukan</th><th>Alasan</th></tr></thead>
            <tbody>{requests.map((request, index) => (
              <tr key={index}>
                <td>{request.source}</td>
                <td className="max-w-md">{(request.required_fields || []).join(", ")}</td>
                <td className="max-w-md">{request.why}</td>
              </tr>))}
            </tbody>
          </table>
        </>)}
    </div>
  );
}

function Evidence({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="purchases-evidence">
      <thead><tr><th>Berkas</th><th>Sheet</th><th>Jenis</th><th>SHA-256</th><th>Periode</th><th>Diperoleh</th></tr></thead>
      <tbody>{rows.map((row, index) => (
        <tr key={index}>
          <td>{row.filename}</td><td>{row.sheet}</td><td>{row.source_type}</td>
          <td className="font-mono">{(row.sha256 || "").slice(0, 16)}…</td>
          <td>{row.period || "—"}</td><td>{row.obtained_at}</td>
        </tr>))}
      </tbody>
    </table>
  );
}
